import { EditorState } from 'prosemirror-state'

import { NodeTypeKey, NodeTypeMap } from '@showrunner/codex'

import { BlockInfo, isNavLinkElement } from '#util/constants'

import { BlockInfoState } from './types'

const DOC_OFFSET = 1

/**
 * When enabled in the script format, we decide whether to display (CONT'D) next
 * to the character's name so long the same character is speaking. The following
 * node types should reset this continuation, since they establish a new
 * speaking context:
 */
const CHARACTER_CONTINUED_CONTEXTS: string[] = [
  NodeTypeMap.SCENE_HEADING,
  NodeTypeMap.TRANSITION,
  NodeTypeMap.NEW_ACT,
  NodeTypeMap.END_OF_ACT,
]

export const verifyCharContd = (text: string, prev: string | null) => {
  const lower = text.trim().toLowerCase()
  const isVoiceover = /\(v\.o\.?\)/.test(lower)
  // would it be overkill to make this logic aware of the contdDisplayText?
  const isManualContd = lower.match(/\(cont.?d\)/)
  const isEmpty = text.length === 0

  // a voiceover suppresses cont'ds
  // other parenthetical modifiers do not (and are stripped prior to comparing) 🙃
  const currentChar = isVoiceover
    ? lower
    : lower.replace(/\([a-zA-Z0-9-.')(\s]+\)$/, '').trim()

  const isContd =
    currentChar === prev?.toLowerCase() && !isManualContd && !isEmpty
  return { currentChar, isContd }
}

export const extractBlockInfo = (state: EditorState): BlockInfoState => {
  const blockInfo: BlockInfoState = []

  let checkCharContd = false
  let prevChar: null | string = null

  state.doc.forEach((page, pageOffset) => {
    if (page.type.name !== NodeTypeMap.PAGE) return

    page.forEach((block, blockOffset) => {
      const {
        attrs: { elementNumber, id },
        nodeSize,
        textContent,
        type: { name: type },
      } = block

      // if id-maker plugin hasn't run yet, short-circuit
      if (!id) return

      const blockName = block.type.spec.displayName ?? ''
      const trimmedText = textContent.trim()
      const isBlank = trimmedText === ''
      const text = isBlank && isNavLinkElement(type) ? blockName : trimmedText
      const pos = blockOffset + pageOffset + DOC_OFFSET

      if (type === NodeTypeMap.SCENE_HEADING) checkCharContd = true
      if (CHARACTER_CONTINUED_CONTEXTS.includes(type)) prevChar = null

      const info: BlockInfo = {
        type: type as NodeTypeKey,
        text,
        pos,
        elementNumber,
        id,
        isBlank,
        nodeSize,
      }

      if (checkCharContd && type === NodeTypeMap.CHARACTER) {
        const { currentChar, isContd } = verifyCharContd(trimmedText, prevChar)
        info.isContd = isContd
        prevChar = currentChar
      }

      if (
        (type === NodeTypeMap.DIALOGUE || type === NodeTypeMap.PARENTHETICAL) &&
        prevChar !== null
      ) {
        info.speaker = prevChar
      }

      blockInfo.push(info)
    })
  })

  return blockInfo
}

// Return the block info at the given position
export function resolveBlockAt(
  blockInfo: BlockInfoState,
  pos: number,
): BlockInfo | null {
  for (const block of blockInfo) {
    if (block.pos <= pos && block.pos + block.nodeSize > pos) {
      return block
    }
  }

  return null
}
